// src/app/core/chatPage/TextGenerationPage/index.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useLocation, useNavigate } from "react-router-dom";
import { TextGenerationViewModel } from "@/app/core/chatPage/chatCopmonents/shared/TextGenerationViewModel";
import type { Message } from "@/app/core/chatPage/chatCopmonents/shared/index";
import { AI_MODELS } from "@/app/core/chatPage/chatCopmonents/shared/constants";
import { ChatTabs } from "@/app/core/chatPage/chatCopmonents/chatInForm/components/ChatTabs";
import { ProfileViewModel } from "@/app/core/tockensPaymentLogic/ProfileViewModel";

export type { Message };

export const TextGenerationPage = observer(() => {
  const AUTO_SCROLL_THRESHOLD_PX = 80;
  const navigate = useNavigate();
  const location = useLocation();
  const viewModel = TextGenerationViewModel.getInstance();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const consumedInitialPromptRef = useRef<string | null>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);

    useEffect(() => {
    const profileViewModel = ProfileViewModel.getInstance();

    viewModel.attachWebSocketHandler();
    viewModel.loadConversations();

    // Сначала грузим профиль, чтобы взять из него дефолтные модели,
    // потом — каталог моделей
    (async () => {
      try {
        await profileViewModel.loadAll();
      } catch (e) {
        console.warn('[TextGenerationPage] profile load failed', e);
      }
      await viewModel.loadAvailableModels();
    })();

    return () => {
      viewModel.detachWebSocketHandler();
    };
  }, [viewModel]);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior,
    });
  };

  const handleMessagesScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distanceToBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceToBottom <= AUTO_SCROLL_THRESHOLD_PX;

    setIsAutoScrollEnabled((previousValue) => {
      if (isNearBottom && !previousValue) {
        window.requestAnimationFrame(() => scrollToBottom("smooth"));
      }
      return isNearBottom;
    });
  };

  

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    if (isAutoScrollEnabled) {
      scrollToBottom(viewModel.isGenerating ? "auto" : "smooth");
    }
  }, [
    isAutoScrollEnabled,
    viewModel.messages.length,
    viewModel.isGenerating,
    viewModel.messages[viewModel.messages.length - 1]?.text,
  ]);

  // Обработка initialPrompt
  useEffect(() => {
    const routeState = location.state as { initialPrompt?: string } | null;
    const initialPrompt = routeState?.initialPrompt?.trim();

    if (!initialPrompt || consumedInitialPromptRef.current === initialPrompt) return;

    consumedInitialPromptRef.current = initialPrompt;
    viewModel.startNewConversation();
    viewModel.handleSend(initialPrompt);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate, viewModel]);

  // Автовыбор модели при переходе с главной
  useEffect(() => {
    const routeState = location.state as {
      selectedModelId?: string;
      modelCategory?: "text" | "image" | "video";
    } | null;
    const modelId = routeState?.selectedModelId;
    const category = routeState?.modelCategory;
    if (!modelId) return;

    if (category) {
      viewModel.setActiveModelType(category);
    }

    const model =
      viewModel.availableModels.find((m) => m.id === modelId) ||
      AI_MODELS.find((m) => m.id === modelId);
    if (model) {
      viewModel.setSelectedModel(model);
    }

    const newState = { ...location.state };
    delete newState.selectedModelId;
    delete newState.modelCategory;
    navigate(location.pathname, { replace: true, state: newState });
  }, [location.state, navigate, viewModel]);

  // Безопасное получение активной беседы
  const activeConversation = useMemo(() => {
  const conversations = viewModel.conversations;
  if (!Array.isArray(conversations)) return null;
  return (
    conversations.find(
      (conversation) => conversation.id === viewModel.activeConversationDbId
    ) || null
  );
}, [viewModel.conversations, viewModel.activeConversationDbId]);

  const modelDisplayNames = useMemo(() => {
    const models = [...AI_MODELS, ...viewModel.availableModels];
    return models.reduce<Record<string, string>>((accumulator, model) => {
      if (model.id && model.name) accumulator[model.id] = model.name;
      return accumulator;
    }, {});
  }, [viewModel.availableModels]);

  const initialTab = (location.state as any)?.initialTab || undefined;

  return (
    <div className="h-full flex flex-col">
      <ChatTabs
        initialTab={initialTab}
        messages={viewModel.messages}
        scrollContainerRef={scrollContainerRef}
        messagesEndRef={messagesEndRef}
        modelDisplayNames={modelDisplayNames}
        activeConversationTitle={activeConversation?.title}
        onRegenerate={(messageId) => viewModel.regenerateResponse(messageId)}
        onScroll={handleMessagesScroll}
      />
    </div>
  );
});